import { APPConfig } from 'config/appConfig'
import {
  checkoutContainer,
  customerContainer,
  storeContainer,
} from 'src/models'
import { getLocaleCodeFromUrl, convertToBoolean } from 'src/utils'
import { countryData } from 'src/views/components/SignUpForm/countryCodeData'

/**
 * Product “EU” legal-tax scope: only these 23 storefronts are supported (ISO 3166-1 alpha-2).
 * nu-taxid EUA components (not Latam JSON) and conversion rules (optional-if-empty, validate-if-filled)
 * both use this single list — no other markets are treated as EU for legal tax.
 */
const EU_LEGAL_TAX_SUPPORTED_MARKETS = [
  'at',
  'be',
  'ch',
  'cz',
  'de',
  'dk',
  'es',
  'fi',
  'fr',
  'gb',
  'hu',
  'ie',
  'is',
  'it',
  'lu',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'se',
  'sk',
  'za',
]

function isInEuLegalTaxSupportedMarkets(countryCode) {
  if (!countryCode) return false
  return EU_LEGAL_TAX_SUPPORTED_MARKETS.includes(
    String(countryCode).toLowerCase()
  )
}

function isEuaLegalTaxMarket(countryCode) {
  return isInEuLegalTaxSupportedMarkets(countryCode)
}

/**
 * English name from SignUpForm `countryCodeData` labels (`Country(+NN)` → `Country`).
 * Matches Jitsu/storefront phone-country naming for EUA legal-resident copy.
 *
 * @param {string} iso2Upper - ISO 3166-1 alpha-2 uppercase
 * @returns {string|undefined}
 */
function getCountryNameFromSignUpCountryData(iso2Upper) {
  const row = countryData.find(c => String(c.value).toUpperCase() === iso2Upper)
  if (!row?.label) {
    return undefined
  }
  const name = String(row.label)
    .trimEnd()
    .replace(/\(\+\d+\)$/, '')
    .trim()
  return name || undefined
}

/**
 * For EUA (23 legal-tax) storefronts, label/error use `ISO - English region name`, e.g. `FI - Finland`.
 * Uses `countryCodeData` first (same source as signup phone list), then `Intl.DisplayNames` if missing.
 * Other markets keep uppercase ISO2 only (legacy behavior).
 *
 * @param {string} countryCode - ISO 3166-1 alpha-2 from locale / config
 * @returns {string}
 */
function getEuaLegalResidentCountryDisplay(countryCode) {
  const code = String(countryCode || '')
    .trim()
    .toUpperCase()
  if (!code) {
    return ''
  }
  if (!isEuaLegalTaxMarket(countryCode)) {
    return code
  }
  const fromSignupData = getCountryNameFromSignUpCountryData(code)
  if (fromSignupData) {
    return `${code} - ${fromSignupData}`
  }
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
    return code
  }
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
    const name = regionNames.of(code)
    if (!name || name === code) {
      return code
    }
    return `${code} - ${name}`
  } catch {
    return code
  }
}

/**
 * Non–tax-registration fields from nu-taxid / tax MFE, aligned with Jitsu
 * `addConditionalPayloadObjects` → `keysExcludedFromLegalTax` (entrepreneur name + keys routed to accountProperties).
 */
const EUA_GTM_NON_TAX_FIELD_KEYS_GLOBAL = new Set(['name'])

/** ISO2 lowercase → extra taxKeys excluded per market (see createAccountUtils taxToAccountProperties). */
const EUA_GTM_NON_TAX_FIELD_KEYS_BY_COUNTRY = {
  be: ['peppol'],
  pl: ['vatPayer'],
  it: ['PecEmail'],
}

/** When country is unknown, exclude every key that can be non–tax-ID anywhere (safe default). */
const EUA_GTM_NON_TAX_FIELD_KEYS_FALLBACK = new Set([
  ...EUA_GTM_NON_TAX_FIELD_KEYS_GLOBAL,
  'peppol',
  'vatPayer',
  'PecEmail',
])

function getEuaGtmNonTaxFieldKeysForCountry(countryCode) {
  if (!countryCode) return EUA_GTM_NON_TAX_FIELD_KEYS_FALLBACK
  const c = String(countryCode).toLowerCase()
  const excluded = new Set(EUA_GTM_NON_TAX_FIELD_KEYS_GLOBAL)
  const extra = EUA_GTM_NON_TAX_FIELD_KEYS_BY_COUNTRY[c] ?? []
  extra.forEach(k => excluded.add(k))
  return excluded
}

/** Same mapping as Jitsu `createAccountUtils` taxToAccountProperties (ISO2 lowercase). */
const EUA_TAX_KEY_TO_ACCOUNT_PROPERTY = {
  be: [{ taxKey: 'peppol', attributeId: 'peppol' }],
  pl: [{ taxKey: 'vatPayer', attributeId: 'vatPayer' }],
  it: [{ taxKey: 'PecEmail', attributeId: 'PecEmail' }],
}

const EUA_ACCOUNT_PROPERTY_ATTRIBUTE_IDS = new Set([
  'peppol',
  'vatPayer',
  'PecEmail',
])

function normalizeUserTaxPrefillRow(row) {
  if (!row || typeof row !== 'object') return null
  const taxKey = row.taxKey != null ? String(row.taxKey) : ''
  if (!taxKey) return null
  return {
    ...row,
    id: row.id != null ? row.id : 0,
    taxKey,
    value: row.value,
  }
}

function isMeaningfulTaxPrefillValue(value) {
  if (value === true || value === false) return true
  if (value == null) return false
  return String(value).trim() !== ''
}

/**
 * Merges split tax GET fields (`name`, `accountProperties`) and profile account properties into
 * a single `userTax`-shaped list so nu-taxid prefill and `checkIsAPICallRequired` match on `taxKey`.
 *
 * @param {Record<string, unknown>} taxResponse - raw getTaxIdData response
 * @param {string} countryCode - storefront ISO2
 * @param {Array<{ attributeId?: string, value?: unknown }>} [profileAccountProperties]
 * @returns {Array<{ id: number, taxKey: string, value: unknown }>}
 */
function enrichTaxIdUserTaxForFormPrefill(
  taxResponse,
  countryCode,
  profileAccountProperties
) {
  if (!taxResponse || typeof taxResponse !== 'object') return []

  let rows = Array.isArray(taxResponse.userTax)
    ? taxResponse.userTax.map(normalizeUserTaxPrefillRow).filter(Boolean)
    : []

  if (!isEuaLegalTaxMarket(countryCode)) {
    return rows
  }

  const existingKeys = new Set(rows.map(r => r.taxKey))

  const addIfMissing = (taxKey, value) => {
    if (taxKey == null || existingKeys.has(taxKey)) return
    if (!isMeaningfulTaxPrefillValue(value)) return
    rows.push({ id: 0, taxKey, value })
    existingKeys.add(taxKey)
  }

  const nameVal = taxResponse.name
  if (isMeaningfulTaxPrefillValue(nameVal)) {
    addIfMissing('name', nameVal)
  }

  const mergeProps = propsList => {
    if (!Array.isArray(propsList)) return
    for (const p of propsList) {
      const id = p?.attributeId
      if (id && EUA_ACCOUNT_PROPERTY_ATTRIBUTE_IDS.has(String(id))) {
        addIfMissing(String(id), p?.value)
      }
    }
  }

  mergeProps(taxResponse.accountProperties)
  mergeProps(profileAccountProperties)

  return rows
}

/**
 * Shallow-clones tax GET response and replaces `userTax` with {@link enrichTaxIdUserTaxForFormPrefill}.
 */
function enrichTaxIdGetResponseForPrefill(
  taxResponse,
  countryCode,
  profileAccountProperties
) {
  if (!taxResponse || typeof taxResponse !== 'object') return taxResponse
  const userTax = enrichTaxIdUserTaxForFormPrefill(
    taxResponse,
    countryCode,
    profileAccountProperties
  )
  return { ...taxResponse, userTax }
}

/**
 * Normalizes a tax value to a safe string for form payloads.
 * - null/undefined => ''
 * - objects => JSON string
 * - string => unchanged
 * - number/boolean/bigint => String(value)
 * - symbol => value.toString()
 * - any other type => ''
 *
 * @param {unknown} value
 * @returns {string}
 */
function toSafeTaxFormValue(value) {
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  if (typeof value === 'string') return value
  switch (typeof value) {
    case 'number':
    case 'boolean':
    case 'bigint':
    case 'symbol':
      return String(value)
    default:
      return ''
  }
}

/**
 * Maps ACCOUNT tax GET `userTax` rows to nu-tax onSubmit shape for merging partial submits.
 *
 * @param {Array<{ id?: number, taxKey?: string, value?: unknown }>} userTax
 * @returns {Array<{ id: number, name: string, taxKey: string, value: string }>}
 */
function mapUserTaxToNuTaxFormRows(userTax) {
  return (Array.isArray(userTax) ? userTax : [])
    .filter(t => String(t?.taxKey ?? '').trim() !== '')
    .map(t => ({
      id: t.id != null ? t.id : 0,
      name: String(t.taxKey),
      taxKey: String(t.taxKey),
      value: toSafeTaxFormValue(t.value),
    }))
}

/**
 * Merge partial nu-tax `onSubmit` rows with baseline GET `userTax` (incoming overwrites per tax key,
 * case-insensitive). Same merge semantics as AF2 mergeTaxDataInfoPayload for storefront tax POST.
 *
 * @param {Array<{ id?: number, taxKey?: string, value?: unknown }>} baselineUserTax
 * @param {Array<{ id?: number, name?: string, taxKey?: string, value?: unknown }>} formRows
 * @returns {Array<{ id: number, name: string, taxKey?: string, value: string }>}
 */
function mergeNuTaxSubmitRowsWithBaselineUserTax(baselineUserTax, formRows) {
  const merged = mapUserTaxToNuTaxFormRows(baselineUserTax)
  const incoming = Array.isArray(formRows) ? formRows : []

  for (const item of incoming) {
    if (item == null) continue
    const keyRaw = item.name ?? item.taxKey ?? ''
    const key = String(keyRaw).trim()
    if (!key) continue
    const lower = key.toLowerCase()
    const idx = merged.findIndex(
      p =>
        String(p?.name ?? p?.taxKey ?? '')
          .trim()
          .toLowerCase() === lower
    )
    const rawVal = item?.value
    const strVal = toSafeTaxFormValue(rawVal)
    const incomingId = item?.id
    if (idx >= 0) {
      merged[idx] = {
        ...merged[idx],
        name: item.name ?? merged[idx].name,
        taxKey: item.taxKey ?? merged[idx].taxKey,
        id: incomingId ?? merged[idx].id ?? 0,
        value: strVal,
      }
    } else {
      merged.push({
        id: incomingId != null ? incomingId : 0,
        name: item.name ?? item.taxKey ?? key,
        taxKey: item.taxKey,
        value: strVal,
      })
    }
  }
  return merged
}

/**
 * For the 23 EU legal-tax markets: split nu-taxid form rows into `userTaxs`, optional root `name`,
 * and optional `accountProperties`, matching Jitsu `addConditionalPayloadObjects` legal-tax handling.
 *
 * @param {Array<{ id?: number, name?: string, value?: unknown }>} data - TaxCommonForm onSubmit payload
 * @param {string} countryCode - storefront ISO2
 * @returns {{ userTaxs: Array<{ id: number, taxKey: string, value: string }>, name?: string, accountProperties?: Array<{ attributeId: string, value: string }> }}
 */
function buildEuaSubmitTaxIdPayloadFromFormData(data, countryCode) {
  const excluded = getEuaGtmNonTaxFieldKeysForCountry(countryCode)
  const c = String(countryCode || '').toLowerCase()
  const accountPropertiesConfig = EUA_TAX_KEY_TO_ACCOUNT_PROPERTY[c] ?? []

  const normalized = (Array.isArray(data) ? data : [])
    .filter(item => item != null)
    .map(item => {
      const rawKey = item?.name
      const taxKey = rawKey != null ? String(rawKey) : ''
      let value = item?.value
      if (typeof value === 'boolean') {
        value = String(value)
      } else if (value == null) {
        value = ''
      } else {
        value = toSafeTaxFormValue(value)
      }
      return {
        id: item?.id != null ? item.id : 0,
        taxKey,
        value,
      }
    })
    .filter(item => item.taxKey !== '')

  const nameField = normalized.find(row => row.taxKey === 'name')
  const nameValue = toSafeTaxFormValue(nameField?.value).trim()
  const name = nameValue !== '' ? nameValue : undefined

  const accountProperties = accountPropertiesConfig
    .map(({ taxKey, attributeId }) => {
      const field = normalized.find(row => row.taxKey === taxKey)
      if (!field) return null
      if (String(field.value).trim() === '') return null
      return { attributeId, value: String(field.value) }
    })
    .filter(Boolean)

  const userTaxs = normalized.filter(row => !excluded.has(row.taxKey))

  return {
    userTaxs,
    ...(name ? { name } : {}),
    ...(accountProperties.length > 0 ? { accountProperties } : {}),
  }
}

/**
 * Normalizes a candidate tax value to trimmed string and returns null when empty.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
function toNonEmptySafeTaxValue(value) {
  const normalized = toSafeTaxFormValue(value).trim()
  return normalized === '' ? null : normalized
}

/**
 * Scans array-form tax payload and returns first non-empty value from non-excluded fields.
 *
 * @param {Array<{ name?: string, value?: unknown }>} taxData
 * @param {Set<string>} excluded
 * @returns {string|null}
 */
function findFirstNonEmptyTaxValueFromArray(taxData, excluded) {
  for (const item of taxData) {
    const fieldName = item?.name
    if (fieldName == null || excluded.has(String(fieldName))) continue
    const normalizedValue = toNonEmptySafeTaxValue(item?.value)
    if (normalizedValue) return normalizedValue
  }
  return null
}

/**
 * Scans object-form tax payload and returns first non-empty value from non-excluded keys.
 *
 * @param {Record<string, unknown>} taxData
 * @param {Set<string>} excluded
 * @returns {string|null}
 */
function findFirstNonEmptyTaxValueFromObject(taxData, excluded) {
  for (const [key, val] of Object.entries(taxData)) {
    if (excluded.has(key)) continue
    const normalizedValue = toNonEmptySafeTaxValue(val)
    if (normalizedValue) return normalizedValue
  }
  return null
}

/**
 * Primary identifier for EUA nu-taxid GTM/analytics capture (RFC_id).
 * Walks the MFE payload in order — first non-empty value whose field name is not excluded
 * (same rules as Jitsu legal-tax vs account property split).
 *
 * @param {Array<{ name?: string, value?: unknown }> | Record<string, unknown>} taxData - payload from TaxCommonForm `onSubmit`
 * @param {string} [countryCode] - storefront ISO2; drives country-specific exclusions (peppol, vatPayer, PecEmail)
 * @returns {string}
 */
function getEuaLegalTaxIdentifierForCapture(taxData, countryCode) {
  const excluded = getEuaGtmNonTaxFieldKeysForCountry(countryCode)
  if (Array.isArray(taxData)) {
    return findFirstNonEmptyTaxValueFromArray(taxData, excluded) ?? 'NA'
  }

  if (!taxData || typeof taxData !== 'object') return 'NA'

  return findFirstNonEmptyTaxValueFromObject(taxData, excluded) ?? 'NA'
}

/**
 * True for the 23 supported EU legal-tax storefronts where conversion tax is optional when blank,
 * but must pass nu-taxid validation when any field in the legal tax block is filled.
 */
function isEuLegalTaxConversionMarket(countryCode) {
  return isInEuLegalTaxSupportedMarkets(countryCode)
}

/**
 * Detects user input anywhere inside the legal tax section (works across EU market-specific forms).
 * @param {HTMLElement} rootElement - DOM element containing the legal tax form (passed via ref)
 */
function legalTaxSectionHasAnyUserInput(rootElement) {
  if (typeof document === 'undefined') return false
  if (!rootElement) return false
  const controls = rootElement.querySelectorAll(
    'input:not([type="hidden"]):not([type="button"]), textarea, select'
  )
  return [...controls].some(el => {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked
    return Boolean(el.value?.trim?.())
  })
}

async function getLatamTaxInputs() {
  const isStaticJsonEnabled = convertToBoolean(
    APPConfig?.getAppConfig()?.enableLegalTaxStaticJson
  )
  const countryCode = getLocaleCodeFromUrl()?.split('_')?.[1] || ''
  const getLegalTaxIdResponse = checkoutContainer.accountsTaxIdResponse || {}
  const profileAccountProperties =
    customerContainer?.profileResponse?.accounts?.properties || []

  const getLocaleAndUserTypeJson = async () => {
    if (isStaticJsonEnabled) {
      try {
        const { default: inputJSON } = await import(
          `src/views/components/CommonFormForCS/LatamInputJSON/${countryCode}`
        )
        return inputJSON()
      } catch (error) {
        console.error(
          `Error loading JSON for country code: ${countryCode}`,
          error
        )
      }
    } else {
      return checkoutContainer?.legalTaxInputJsonResponse
    }
  }

  let finalInputJSON = await getLocaleAndUserTypeJson()
  let isUserDataExists = false
  const accountType =
    customerContainer?.profileResponse?.accounts?.accountType || 'Guest User'

  const userTaxRows = enrichTaxIdUserTaxForFormPrefill(
    getLegalTaxIdResponse,
    countryCode,
    profileAccountProperties
  )

  if (
    userTaxRows.length > 0 &&
    finalInputJSON?.[accountType] &&
    Array.isArray(finalInputJSON[accountType])
  ) {
    const mergedData = finalInputJSON[accountType].map(item => {
      const matchingTaxId = userTaxRows.find(
        taxData => taxData?.taxKey === item?.name
      )
      if (matchingTaxId) {
        if (item?.type === 'checkbox') {
          const raw = matchingTaxId.value
          const checked =
            raw === true ||
            raw === 'true' ||
            raw === 'Y' ||
            raw === 'y' ||
            raw === 1 ||
            raw === '1'
          return {
            ...item,
            defaultChecked: checked,
          }
        }
        return {
          ...item,
          defaultValue: matchingTaxId.value,
        }
      }
      return item
    })
    finalInputJSON = {
      ...finalInputJSON,
      [accountType]: mergedData,
    }
    const isMandatoryFieldPresent = mergedData?.some(
      item =>
        item?.requiredTypes &&
        item?.requiredTypes.length > 0 &&
        item?.requiredTypes.includes('checkout', 'account')
    )
    mergedData?.forEach(taxItems => {
      const hasCheckboxValue =
        taxItems?.type === 'checkbox' &&
        typeof taxItems?.defaultChecked === 'boolean'
      if (
        taxItems?.type !== 'radio' &&
        taxItems?.type !== 'hidden' &&
        (hasCheckboxValue ||
          (taxItems?.defaultValue !== '' &&
            taxItems?.defaultValue !== undefined)) &&
        (isMandatoryFieldPresent || !isMandatoryFieldPresent)
      ) {
        isUserDataExists = true
      }
    })
  }
  return { finalInputJSON, isUserDataExists }
}

function checkIsAPICallRequired(props) {
  const { data, formInputJson, getCallData, taxGetResponse, countryCode } =
    props
  const country = countryCode || getLocaleCodeFromUrl()?.split('_')?.[1] || ''
  const profileAccountProperties =
    customerContainer?.profileResponse?.accounts?.properties || []
  const prefillSource =
    taxGetResponse && typeof taxGetResponse === 'object'
      ? taxGetResponse
      : { userTax: Array.isArray(getCallData) ? getCallData : [] }
  const effectiveGetCallData = enrichTaxIdUserTaxForFormPrefill(
    prefillSource,
    country,
    profileAccountProperties
  )

  const apiCallTriggerCheck = formInputJson?.some(item => {
    if (item?.type === 'radio') return false

    const matchingKey = data.find(key => key.name === item.name)
    if (!matchingKey) return false

    if (effectiveGetCallData && effectiveGetCallData.length > 0) {
      return effectiveGetCallData.some(getData => {
        if (getData?.taxKey === matchingKey.name) {
          return true
        } else {
          return matchingKey?.name && matchingKey?.value !== ''
        }
      })
    } else {
      return matchingKey?.value !== ''
    }
  })

  return apiCallTriggerCheck
}

const isHomeCountry = () => {
  let homeCountry = storeContainer.currentRegion || ''
  let isHomeCountry = false
  customerContainer?.profileResponse?.accounts?.properties?.some(
    (element = {}) => {
      if (
        element.attributeId === 'countryCode' &&
        element.value === homeCountry
      ) {
        isHomeCountry = true
      }
    }
  )
  return isHomeCountry
}

export default getLatamTaxInputs
export {
  getLatamTaxInputs,
  checkIsAPICallRequired,
  isHomeCountry,
  isEuaLegalTaxMarket,
  getEuaLegalResidentCountryDisplay,
  getEuaLegalTaxIdentifierForCapture,
  isEuLegalTaxConversionMarket,
  legalTaxSectionHasAnyUserInput,
  EU_LEGAL_TAX_SUPPORTED_MARKETS,
  buildEuaSubmitTaxIdPayloadFromFormData,
  enrichTaxIdUserTaxForFormPrefill,
  enrichTaxIdGetResponseForPrefill,
  mergeNuTaxSubmitRowsWithBaselineUserTax,
}
